import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { isValidEmail, isPasswordValid } from '../../utils/authValidation';
import { API_BASE } from '../../config/api';
import { Eyebrow } from '../Layout/PageLayout';
import {
  AuthPage,
  AuthLayout,
  AuthIntro,
  IntroNote,
  AuthCard,
  CardBody,
  CardFoot,
  PanelTitle,
  PanelText,
  Form,
  Field,
  Input,
  FieldError,
  MessageBox,
  PrimaryButton,
  GoogleButton,
  Divider,
  PasswordChips,
  InlineLink,
  googleStartUrl,
} from './AuthSharedStyles';

const StepHead = styled.div`
  padding: 1.25rem 2rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;

const StepBars = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.375rem;

  span {
    height: 3px;
    border-radius: 2px;
    background: var(--brand);
  }

  span:last-child {
    background: ${(p) => (p.$step === 2 ? 'var(--brand)' : 'var(--border)')};
  }
`;

const EmailChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem 0.625rem 1rem;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;

  span {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--text-code);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    min-height: 2.25rem;
    padding: 0 0.75rem;
    background: transparent;
    border: 1px solid var(--border-accent);
    border-radius: 0.375rem;
    color: var(--text-primary);
    font-size: 0.8125rem;
  }
`;

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { login } = useAuth();

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const mismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = isValidEmail(email) && isPasswordValid(password) && passwordsMatch;

  const goToPassword = (e) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setStep(2);
  };

  const backToEmail = () => {
    setError('');
    setPassword('');
    setConfirmPassword('');
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet all requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const registerResponse = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!registerResponse.ok) {
        const data = await registerResponse.json();
        throw new Error(data.error || 'Registration failed');
      }

      const loginResponse = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
        credentials: 'include',
      });

      if (!loginResponse.ok) {
        const data = await loginResponse.json();
        throw new Error(data.error || 'Login failed after registration');
      }

      const loginData = await loginResponse.json();
      login(email.trim(), loginData.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage>
      <AuthLayout $top>
        <AuthIntro style={{ paddingTop: '1.5rem' }}>
          <Eyebrow>Account</Eyebrow>
          <h1>Create your account.</h1>
          <p>Your purchases and downloads are saved to your account.</p>
          <IntroNote>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }}>
              <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
            </svg>
            <p>Use a strong password to protect your downloads.</p>
          </IntroNote>
        </AuthIntro>

        <AuthCard>
          <StepHead>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>STEP {step} OF 2</span>
              <span>{step === 1 ? 'Your email' : 'Password'}</span>
            </div>
            <StepBars $step={step} aria-hidden="true">
              <span />
              <span />
            </StepBars>
          </StepHead>

          <CardBody>
            {step === 1 ? (
              <>
                <PanelTitle>Create your account</PanelTitle>
                <PanelText>It takes less than a minute.</PanelText>
                {error && (
                  <MessageBox $error role="alert">
                    {error}
                  </MessageBox>
                )}
                <GoogleButton href={googleStartUrl()}>
                  <span aria-hidden="true">G</span>
                  Continue with Google
                </GoogleButton>
                <Divider>or with email</Divider>
                <Form onSubmit={goToPassword}>
                  <Field>
                    <label htmlFor="reg-email">Email</label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <PrimaryButton type="submit">
                    Continue
                    <Arrow />
                  </PrimaryButton>
                </Form>
              </>
            ) : (
              <>
                <PanelTitle style={{ marginBottom: '1rem' }}>Choose a password</PanelTitle>
                <EmailChip>
                  <span>{email.trim()}</span>
                  <button type="button" onClick={backToEmail}>
                    Change
                  </button>
                </EmailChip>
                {error && (
                  <MessageBox $error role="alert">
                    {error}
                  </MessageBox>
                )}
                <Form onSubmit={handleSubmit}>
                  <Field>
                    <label htmlFor="reg-password">Password</label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      aria-describedby="reg-reqs"
                      autoFocus
                      required
                    />
                  </Field>
                  <PasswordChips id="reg-reqs" password={password} />
                  <Field>
                    <label htmlFor="reg-confirm">Confirm password</label>
                    <Input
                      id="reg-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      $invalid={mismatch}
                      required
                    />
                    {mismatch && <FieldError>The passwords do not match.</FieldError>}
                  </Field>
                  <PrimaryButton type="submit" disabled={loading || !canSubmit}>
                    {loading ? 'Creating account…' : 'Create account'}
                  </PrimaryButton>
                </Form>
              </>
            )}
          </CardBody>

          <CardFoot>
            Already have an account? <InlineLink to="/login">Log in</InlineLink>
          </CardFoot>
        </AuthCard>
      </AuthLayout>
    </AuthPage>
  );
};

export default Register;
